import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../utils/apiConfig';

/**
 * AvailableUsers - Widget "Personnes disponibles" pour Planificateur
 */
const AvailableUsers = ({ token, onNavigate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAvailableUsers = async () => {
      if (!token) return;

      setLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/dashboard/available-users`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (!response.ok) throw new Error('Erreur de chargement');

        const result = await response.json();
        setData(result.success ? result.data : null);
      } catch (err) {
        console.error('Erreur fetch available users:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailableUsers();
  }, [token]);

  if (loading) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Personnes disponibles</h4>
        <div style={styles.loading}>Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Personnes disponibles</h4>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  const users = data?.users || [];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h4 style={styles.title}>Personnes disponibles</h4>
        <button
          style={styles.viewButton}
          onClick={() => onNavigate && onNavigate('planning')}
        >
          Voir planning
        </button>
      </div>

      <div style={styles.weekBadge}>
        Semaine en cours : {users.length} pers.
      </div>

      {users.length === 0 ? (
        <div style={styles.empty}>
          <span style={styles.emptyIcon}>👥</span>
          <p style={styles.emptyText}>Tout le monde est occupé cette semaine</p>
        </div>
      ) : (
        <div style={styles.list}>
          {users.slice(0, 8).map((user) => (
            <div key={user.id} style={styles.userItem}>
              <span style={styles.userIcon}>👤</span>
              <span style={styles.userName}>{user.name}</span>
              {user.group_name && (
                <span style={styles.groupBadge}>{user.group_name}</span>
              )}
            </div>
          ))}
          {users.length > 8 && (
            <div style={styles.moreUsers}>
              +{users.length - 8} autres disponibles
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px'
  },
  title: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1a1a2e'
  },
  viewButton: {
    background: 'none',
    border: 'none',
    color: '#6366f1',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500'
  },
  weekBadge: {
    display: 'inline-block',
    padding: '6px 12px',
    backgroundColor: '#d4edda',
    color: '#155724',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: '500',
    marginBottom: '16px'
  },
  loading: {
    textAlign: 'center',
    padding: '30px',
    color: '#6c757d'
  },
  error: {
    padding: '20px',
    backgroundColor: '#fff5f5',
    borderRadius: '8px',
    color: '#dc3545',
    textAlign: 'center'
  },
  empty: {
    textAlign: 'center',
    padding: '30px'
  },
  emptyIcon: {
    fontSize: '2rem',
    display: 'block',
    marginBottom: '8px'
  },
  emptyText: {
    margin: 0,
    color: '#6c757d'
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  userItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px'
  },
  userIcon: {
    fontSize: '0.9rem'
  },
  userName: {
    fontSize: '0.9rem',
    color: '#1a1a2e',
    flex: 1
  },
  groupBadge: {
    fontSize: '0.75rem',
    padding: '2px 8px',
    backgroundColor: '#e9ecef',
    borderRadius: '4px',
    color: '#495057'
  },
  moreUsers: {
    textAlign: 'center',
    padding: '8px',
    fontSize: '0.85rem',
    color: '#6c757d',
    fontStyle: 'italic'
  }
};

export default AvailableUsers;
